import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { publicSettings } from '../services/publicSettings';
import { loadGoogleMaps, gmapsAuthFailed } from '../services/googleMapsLoader';

/**
 * AddressAutocomplete — Google Places-backed input.
 * Falls back to Nominatim (OSM) if no Google Maps API key configured.
 * Calls onChange(addressString, { lat, lng, place_id? }) when user picks a suggestion.
 */
const AddressAutocomplete = ({ value = '', onChange, placeholder = 'Search Ottawa or Ibadan address…', label, icon: Icon = MapPin, autoFocus, className = '', disabled = false }) => {
  const inputRef = useRef(null);
  const [val, setVal] = useState(value);
  const [loaded, setLoaded] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSugg, setShowSugg] = useState(false);
  const [searching, setSearching] = useState(false);
  const [provider, setProvider] = useState(null); // 'google' | 'osm'
  const abortRef = useRef(null);
  const typedRef = useRef(false);

  useEffect(() => { setVal(value || ''); }, [value]);

  // If Google auth/billing fails mid-session we flip to OSM — re-run the
  // search ONLY if the user was actively typing (never auto-open on mount
  // for a pre-filled saved address).
  useEffect(() => {
    if (provider === 'osm' && typedRef.current && val && val.length >= 3) {
      setShowSugg(true);
      queryOSM(val);
    }
    // eslint-disable-next-line
  }, [provider]);

  // Init: try Google first, else OSM fallback
  useEffect(() => {
    let mounted = true;
    publicSettings.get().then(async (s) => {
      const key = s?.google_maps_api_key;
      const g = key ? await loadGoogleMaps(key).catch(() => null) : null;
      if (!mounted) return;
      if (g?.maps?.places && inputRef.current && !gmapsAuthFailed()) {
        const ac = new g.maps.places.Autocomplete(inputRef.current, {
          fields: ['geometry', 'formatted_address', 'place_id'],
          // ShipitNow operates in Ottawa (Canada) and Ibadan (Nigeria) only.
          componentRestrictions: { country: ['ca', 'ng'] },
        });
        ac.addListener('place_changed', () => {
          const p = ac.getPlace();
          const addr = p.formatted_address || inputRef.current.value;
          const lat = p.geometry?.location?.lat?.();
          const lng = p.geometry?.location?.lng?.();
          setVal(addr);
          onChange?.(addr, { lat, lng, place_id: p.place_id });
        });
        setProvider('google'); setLoaded(true);
        // Health-probe Places: billing-disabled keys load the script fine but
        // deny requests. One tiny prediction call tells us to fall back to OSM.
        try {
          const svc = new g.maps.places.AutocompleteService();
          svc.getPlacePredictions({ input: 'Ottawa' }, (_preds, status) => {
            if (mounted && status !== 'OK' && status !== 'ZERO_RESULTS') setProvider('osm');
          });
        } catch (e) { if (mounted) setProvider('osm'); }
      } else {
        setProvider('osm'); setLoaded(true);
      }
    });
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  // If Google's auth/billing fails (even after we picked the google provider),
  // flip to the OSM fallback so the input keeps working.
  useEffect(() => {
    const onFail = () => setProvider('osm');
    if (gmapsAuthFailed()) { setProvider('osm'); return undefined; }
    window.addEventListener('gmaps-auth-failed', onFail);
    return () => window.removeEventListener('gmaps-auth-failed', onFail);
  }, []);

  const queryOSM = async (q) => {
    if (!q || q.length < 3) { setSuggestions([]); return; }
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setSearching(true);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=6&countrycodes=ca,ng&q=${encodeURIComponent(q)}`, { signal: ctrl.signal, headers: { 'Accept-Language': 'en' } });
      const data = await r.json();
      setSuggestions(data.map((d) => ({ label: d.display_name, lat: parseFloat(d.lat), lng: parseFloat(d.lon) })));
    } catch (e) { /* aborted */ }
    finally { setSearching(false); }
  };

  const onTextChange = (e) => {
    const v = e.target.value;
    typedRef.current = true;
    setVal(v);
    onChange?.(v, {});
    if (provider === 'osm') {
      setShowSugg(true);
      queryOSM(v);
    }
  };

  const pickOSM = (s) => {
    setVal(s.label); setShowSugg(false); setSuggestions([]);
    onChange?.(s.label, { lat: s.lat, lng: s.lng });
  };

  return (
    <div className={`relative ${className}`}>
      {label && <div className="text-xs text-slate-600 mb-1">{label}</div>}
      <div className="relative">
        {Icon && <Icon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />}
        <Input
          ref={inputRef}
          value={val}
          onChange={onTextChange}
          onFocus={() => provider === 'osm' && val && setShowSugg(true)}
          onBlur={() => setTimeout(() => setShowSugg(false), 200)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className={`h-10 ${Icon ? 'pl-9' : ''}`}
          autoComplete="off"
          disabled={disabled}
        />
        {searching && <Loader2 className="h-3.5 w-3.5 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />}
      </div>
      {provider === 'osm' && showSugg && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-lg border border-slate-200 shadow-lg max-h-72 overflow-y-auto">
          {suggestions.map((s, i) => (
            <button key={i} type="button" onMouseDown={(e) => { e.preventDefault(); pickOSM(s); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm text-slate-700 border-b border-slate-100 last:border-b-0">
              {s.label}
            </button>
          ))}
        </div>
      )}
      {!loaded && <div className="text-[11px] text-slate-400 mt-1">Initializing address search…</div>}
    </div>
  );
};

export default AddressAutocomplete;
